/* Libraries / React Components / Node_Modules */
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import moment from "moment";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";

/* Local Imports */
import Pagination from "../Pagination/Pagination";
import BasicLineColumn from "../common/apexChart/lineChart";

import NoData from "../common/noDataComponetnt";

/* Data */
import { fetchPastebinResult } from "../../actions/pastebin";

// Pastebin Search PAGE (minimal design-first skeleton)
const PasteBin = () => {
  const dispatch = useDispatch();

  // Basic identifiers
    const auth = useSelector((state) => state?.auth?.user);
    const clientType = useSelector((state) => state?.auth?.user?.accessLevel);
    const id = useSelector(state => 
      state?.summary?.localClient?._id || state?.summary?.clients?.data?.[0]?._id
    );
    const client = useSelector(state => 
      state?.summary?.localClient || state?.summary?.clients?.data?.[0]
    );

  // Data and status
  const state = useSelector((state) => state?.pastebin?.response?.data);
  const scanCompleted = useSelector((state) => state?.pastebin?.scanCompleted);
  const oldDnsDetails = useSelector(
    (state) => state?.pastebin?.response?.scanDetailArray
  );

  // =============== State Management (UI only) ===============
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [page, setPage] = useState(1);
  const [PageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [containerClass, setContainerClass] = useState("data-container");

  // ACTION_MANAGEMENT
  const [forValue, setForValue] = useState("forever");
  const [actionNote, setActionNote] = useState("");
  const [addedRemediations, setAddedRemediations] = useState([]);
  const [selectedValue, setSelectedValue] = useState("pleaseSelect");
  const [action, setAction] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [multiCheckTrue, setMultiCheckTrue] = useState(false);

  // =============== Effects (safe to keep, fetches if wired) ===============
  useEffect(() => {
    // Optional: keep data fetching wired, but design works without it too
    if (id) fetchPastebinResult(id, dispatch);
  }, [id, dispatch]);

  // Update total items when data changes
  useEffect(() => {
    setTotalItems(totalItems);
  }, [totalItems]);

  // Update container class when state is loaded
  useEffect(() => {
    if (state) {
      setTimeout(() => {
        setContainerClass("data-container active");
      }, 300);
    }
  }, [state]);

  // ====== COMPUTED VALUES & CONSTANTS (display only) ======
  const ipArray = useMemo(() => {
    const len = oldDnsDetails?.scanDetails?.length || 0;
    const arr = [];
    for (let i = 0; i < len; i++) {
      arr.push(oldDnsDetails?.scanDetails?.[i]?.length || 0);
    }
    return arr;
  }, [oldDnsDetails?.scanDetails]);

  const totalIp = oldDnsDetails?.ipLength || 0;
  const plainPasswords = oldDnsDetails?.plainPassword || 0;
  const plainPasswordPercent =
    ((plainPasswords || 0) / (totalIp || 0 || 1)) * 100;
  const breachedPercent = 0; // placeholder

  const response = useMemo(() => {
    const data = Array.isArray(state) ? [...state] : [];
    return data.sort((a, b) => (a.dec > b.dec ? -1 : b.dec > a.dec ? 1 : 0));
  }, [state]);

  const mapData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return response;
    return response.filter((row) =>
      (row?.result?.[0]?.title || "").toLowerCase().includes(q)
    );
  }, [response, search]);

  const currentTableData = useMemo(() => {
    if (!mapData || mapData.length === 0) return [];
    const firstPageIndex = (page - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return mapData.slice(firstPageIndex, lastPageIndex);
  }, [mapData, page, PageSize]);

 // ====== Checkbox Handlers ======
    // Multiple item selection handler
    const multicheck = (data) => {
      const allIds = data?.map((item) => item._id);
      if (selectedIds.length > 0 || selectedIds.length == 0) {
        setMultiCheckTrue(true);
        setSelectedIds(allIds);
      }
      if (multiCheckTrue == true) {
        setSelectedIds([]);
        setMultiCheckTrue(false);
      }
    };
    // Single item selection handler
    const singleClick = (id, data) => {
      setSelectedIds((prevSelectedIds) => {
        if (prevSelectedIds.includes(id)) {
          return prevSelectedIds.filter(
            (selectedId) => selectedId !== id);
        } else {
          return [...prevSelectedIds, id];
        }
      });
      if (selectedIds.length > 10) {
        setMultiCheckTrue(true);
      } else {
        setMultiCheckTrue(false);
      }
    };
  // ====== END Checkbox Handlers ======
  
  // ====== Export/Download (simple placeholder) ======
  const downloadFile = () => {
    // Minimal placeholder export with only headers, no rows (safe default)
    const ws = XLSX.utils.aoa_to_sheet([["Title", "Link", "Snippet", "Date"]]);
    const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    FileSaver.saveAs(data, "PastebinDetails.xlsx");
  };

  return (
    <div>
      {/* Header + Download within narrowed width */}
      <div className={containerClass}>

        {/* Top Section - Cards + Chart (display-only) */}
        <div className="row clearfix no_margin" style={{ marginTop: "20px" }}>
          <div className="col-sm-2"></div>

          {/* Left Card Box */}
          <div className="col-sm-3">
            <div className="card w_card3">
              <div className="body" style={{ minHeight: "460px" }}>
                {/* Record Counter */}
                <div className="row text-center">
                  <div className="col-12 pb-4 pt-4">
                    <label className="mb-0">Records Found</label>
                    <h4 className="font-30 font-weight-bold text-col-blue">
                      {totalIp || 0}
                    </h4>
                  </div>
                </div>
                {/* Plain Password Counter */}
                <div className="form-group">
                  <label className="d-block">
                    Plain Text Password{" "}
                    <span className="float-right">
                      {plainPasswordPercent?.toFixed(0)}%
                    </span>
                  </label>
                  <div className="progress progress-xxs">
                    <div
                      className="progress-bar progress-bar-success"
                      role="progressbar"
                      aria-valuenow={plainPasswordPercent?.toFixed(0)}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ width: plainPasswordPercent?.toFixed(0) + "%" }}
                    ></div>
                  </div>
                </div>
                {/* Link Scan Counter */}
                <div className="form-group">
                  <label className="d-block">
                    Links scanned in last 30 days{" "}
                    <span className="float-right">
                      {breachedPercent?.toFixed(0)}%
                    </span>
                  </label>
                  <div className="progress progress-xxs">
                    <div
                      className="progress-bar progress-bar-warning"
                      role="progressbar"
                      aria-valuenow={breachedPercent?.toFixed(0)}
                      aria-valuemin="0"
                      aria-valuemax="100"
                      style={{ width: breachedPercent?.toFixed(0) + "%" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Card Box */}
          <div className="col-sm-6">
            <div className="card">
              <div className="body" style={{ minHeight: "460px" }}>
                <div>Previous scan details</div>
                {oldDnsDetails && (
                  <BasicLineColumn
                    dates={oldDnsDetails?.dates}
                    ipArray={ipArray}
                    color={["#6574cd"]}
                    yAxis="Records Found"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section - Table */}
        <div className="row clearfix no_margin" style={{ marginTop: "20px" }}>
          <div className="col-sm-2"></div>
          <div className="col-sm-9" style={{ padding: "0" }}>
            {/* Tab Menu (UI only) */}
            <ul className="nav nav-tabs mb-2" id="myTabs" role="tablist">
              <li className="nav-item">
                <a
                  className={`nav-table-tab nav-link ${category === "all" ? "active" : ""}`}
                  onClick={() => {
                    setCategory("all");
                    setPage(1);
                    setSearch("");
                  }}
                >
                  Current
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link nav-table-tab ${category === "risk-accepted" ? "active" : ""}`}
                  onClick={() => {
                    setCategory("risk-accepted");
                    setPage(1);
                    setSearch("");
                  }}
                >
                  Risk Accepted
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link nav-table-tab ${category === "false-positive" ? "active" : ""}`}
                  onClick={() => {
                    setCategory("false-positive");
                    setPage(1);
                    setSearch("");
                  }}
                >
                  False Positive
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link nav-table-tab ${category === "remidiated" ? "active" : ""}`}
                  onClick={() => {
                    setCategory("remidiated");
                    setPage(1);
                    setSearch("");
                  }}
                >
                  Remediated
                </a>
              </li>
            </ul>

            {/* Search & Download Bar */}
            <div className="row mb-2">
              <div className="col-sm-4">
                <div
                  data-test="datatable-input"
                  className="mdb-datatable-filter flex-row"
                >
                  <input
                    className="form-control form-control-sm ml-0 my-1"
                    type="text"
                    placeholder="Search"
                    aria-label="Search"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="col-md-1 mt-1 text-left hidden-xs">
                {client?.subscriptionType !== "trial" && (
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      downloadFile();
                    }}
                    href="/add-report"
                    className="btn btn-primary mr-1"
                    style={{ color: "black", fontWeight: "bold" }}
                  >
                    <i className="fa fa-file-excel-o" style={{ color: "black" }}></i>
                  </a>
                )}
              </div>
            </div>

            {/* TABLE */}
            <div className="row_boxes row_boxes_table">
              <div className="table_box">
                <table className="table">
                  <thead>
                    <tr>
                      {auth?.accessLevel != "Threat Analyst" && (category === "all" ||
                        category === "risk-accepted" ||
                        category === "false-positive") && (
                          <th>
                            <input
                              type="checkbox"
                              checked={
                                selectedIds == 0 ? false : multiCheckTrue
                              }
                              onClick={() => {
                                multicheck(currentTableData);
                              }}
                              className="checkUncheck"
                            />
                          </th>
                        )}
                      <th>#</th>
                      <th style={{ width: "200px" }}>Title</th>
                      <th>Link</th>
                      <th>Snippet</th>
                      <th>Last modified on</th>
                      {auth?.accessLevel != "Threat Analyst" && (category === "all" ||
                          category === "risk-accepted" ||
                          category === "false-positive") && (
                            <th style={{ width: "80" }}>Action</th>
                          )}
                        <th></th>
                    </tr>
                  </thead>
                  <tbody>
                      {currentTableData.map((row, index) => {
                        const title = row?.result?.[0]?.title || "NA";
                        const snippet = row?.result?.[0]?.snippet || "NA";
                        const link = row?.result?.[0]?.link || "";
                        const pageCount = (page - 1) * 10 + 1;
                        return (
                          <tr key={index}>
                            {auth?.accessLevel != "Threat Analyst" && (category === "all" ||
                              category === "risk-accepted" ||
                              category === "false-positive") && (
                                <td style={{ whiteSpace: "unset" }}>
                                  <input
                                    onClick={() => singleClick(row?._id)}
                                    checked={selectedIds?.includes(
                                      row?._id
                                    )}
                                    type="checkbox"
                                    className="checkUncheck"
                                  />
                                </td>
                              )}
                            <td style={{ whiteSpace: "unset" }}>
                              <span>{pageCount + index}</span>
                            </td>
                            <td
                              style={{
                                whiteSpace: "normal",
                                width: "200px",
                                overflowWrap: "anywhere",
                              }}
                            >
                              <span
                                style={{
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {title}
                              </span>
                            </td>

                            {/*<td
                              style={{
                                width: "120px",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-all",
                              }}
                            >
                              {link ? (
                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ wordBreak: "break-all" }}
                                >
                                  {link}
                                </a>
                              ) : (
                                "NA"
                              )}
                            </td>*/}
                            <td style={{
                                width: "120px",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-all",
                              }}
                            >
                              {link ? (
                                <a
                                  href={link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ wordBreak: "break-all" }}
                                >
                                  {link}
                                </a>
                              ) : (
                                "NA"
                              )}
                            </td>
                            <td
                              style={{
                                width: "200px",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                              }}
                            >
                              <span
                                style={{
                                  whiteSpace: "pre-wrap",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {snippet}
                              </span>
                            </td>
                            <td style={{ whiteSpace: "unset" }}>
                              <span className="badge badge-success">
                                {row?.createdAt
                                  ? moment(row?.createdAt).format("DD-MM-YYYY")
                                  : "NA"}
                              </span>
                            </td>
                            
                          </tr>
                        );
                      })
                      }
                    {/* If not trial and there is no data, the table body will be empty (desired behavior) */}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="dataTables_paginate">
                {totalItems > 0 && (
                  <Pagination
                    className="pagination-bar"
                    currentPage={page}
                    totalCount={totalItems}
                    pageSize={PageSize}
                    onPageChange={(page) => setPage(page)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasteBin;